import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Booking } from '../../api';
import { Card, Avatar } from '../ui';
import { useSettings } from '../../context/SettingsContext';
import { parseUtc } from '../../utils/dates';
import { useActiveRentals } from './useActiveRentals';
import BookingEditModal from '../BookingEditModal';
import RowTooltip from './RowTooltip';

function getInitials(name: string | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2);
  return (parts[0][0] ?? '') + (parts[parts.length - 1][0] ?? '');
}

function describeReturn(endsAt: string, tz: string): { primary: string; secondary: string; isToday: boolean } {
  const end = parseUtc(endsAt);
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrowStart = new Date(todayStart.getTime() + 86_400_000);
  const dayAfter = new Date(tomorrowStart.getTime() + 86_400_000);

  const time = end.toLocaleString('da-DK', { hour: '2-digit', minute: '2-digit', timeZone: tz });

  if (end >= todayStart && end < tomorrowStart) {
    const diffMs = end.getTime() - now.getTime();
    if (diffMs <= 0) return { primary: time, secondary: 'forsinket', isToday: true };
    const hours = Math.floor(diffMs / 3_600_000);
    const minutes = Math.floor((diffMs % 3_600_000) / 60_000);
    const inLabel = hours > 0 ? `om ${hours}t ${minutes}m` : `om ${minutes}m`;
    return { primary: time, secondary: `i dag · ${inLabel}`, isToday: true };
  }
  if (end >= tomorrowStart && end < dayAfter) {
    return { primary: time, secondary: 'i morgen', isToday: false };
  }
  const dateStr = end.toLocaleDateString('da-DK', { day: 'numeric', month: 'short' });
  return { primary: time, secondary: dateStr, isToday: false };
}

export default function UpcomingReturnsWidget() {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const tz = settings.timezone || 'Europe/Copenhagen';

  const { data, loading, error, refresh } = useActiveRentals();
  const [selected, setSelected] = useState<Booking | null>(null);

  // Sortér efter ends_at ASC (soonest return først), tag de første 5
  const items = (data ?? [])
    .slice()
    .sort((a, b) => a.ends_at.localeCompare(b.ends_at))
    .slice(0, 5);

  const handleSaved = (updated: Partial<Booking>) => {
    if (!selected) return;
    refresh();
    setSelected(null);
    void updated;
  };

  return (
    <>
      <Card padded className="h-full">
        <div className="flex items-baseline justify-between mb-1">
          <h3 className="text-base font-semibold text-ink-900 tracking-tight">
            {t('dashboard.upcoming_returns_title', { defaultValue: 'Næste afleveringer' })}
          </h3>
          {items.length > 0 && (
            <span className="text-xs text-ink-500">{items.length} forestående</span>
          )}
        </div>

        {loading && (
          <p className="text-sm text-ink-500 mt-4">…</p>
        )}
        {error && (
          <p className="text-sm text-rose-700 mt-4">Kunne ikke hente data</p>
        )}
        {!loading && !error && items.length === 0 && (
          <p className="text-sm text-ink-500 mt-4">Ingen aktive udlejninger</p>
        )}

        <div className="mt-3 -mx-1">
          {items.map(item => {
            const when = describeReturn(item.ends_at, tz);
            const days = item.rental_duration_hours != null
              ? Math.max(1, Math.round(item.rental_duration_hours / 24))
              : null;
            const fullStart = parseUtc(item.starts_at).toLocaleString('da-DK', {
              day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: tz,
            });
            const fullEnd = parseUtc(item.ends_at).toLocaleString('da-DK', {
              day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: tz,
            });
            return (
              <button
                key={item.id}
                onClick={() => setSelected(item)}
                className={`relative group/row w-full text-left flex items-center gap-3 px-[15px] py-3 border-b border-cream-100 last:border-b-0 hover:bg-cream-100 transition-colors`}
              >
                <Avatar initials={getInitials(item.renter_name)} size="md" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-ink-900 truncate">
                    {item.renter_name || '—'}
                  </div>
                  <div className="text-xs text-ink-500 truncate font-mono">
                    {item.registration_number ?? '—'}
                    {days != null && (
                      <span className="font-sans"> · {days} dages udlejning</span>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className={`text-sm font-semibold text-ink-900`}>
                    {when.primary}
                  </div>
                  <div className="text-xs text-ink-500">{when.secondary}</div>
                </div>

                <RowTooltip>
                  <div className="font-semibold mb-1">{item.renter_name || '—'}</div>
                  <div className="font-mono text-ink-500 mb-1.5">{item.registration_number ?? '—'}</div>
                  <div className="space-y-0.5">
                    <div><span className="text-ink-500">Start:</span> {fullStart}</div>
                    <div><span className="text-ink-500">Aflev:</span> {fullEnd}</div>
                  </div>
                </RowTooltip>
              </button>
            );
          })}
        </div>
      </Card>

      {selected && (
        <BookingEditModal
          booking={selected}
          onClose={() => setSelected(null)}
          onSaved={handleSaved}
        />
      )}
    </>
  );
}
